import React, {useState} from 'react'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {getCategories, createCategory, updateCategory, deleteCategory} from './core/_requests'
import {Category} from './core/_models'
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {toast} from 'react-toastify'
import {DeleteModal} from '../../../../_component/table/DeleteModal'
import {Plus, Pencil, Trash2, Check, X} from 'lucide-react'
import {Skeleton} from '../../../../_component/Skeleton'

const CategoriesList = () => {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | number | null>(null)
  const [editName, setEditName] = useState('')
  const [newName, setNewName] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [deleteId, setDeleteId] = useState<string | number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const {data: categories, isLoading} = useQuery(['categories'], getCategories)

  const createMutation = useMutation(createCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories'])
      setIsAdding(false)
      setNewName('')
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create category'
      toast.error(message)
    },
  })

  const updateMutation = useMutation(
    ({id, name}: {id: string | number; name: string}) => updateCategory(id, {name}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['categories'])
        setEditingId(null)
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Failed to update category'
        toast.error(message)
      },
    }
  )

  const deleteMutation = useMutation(deleteCategory, {
    onSuccess: () => {
      queryClient.invalidateQueries(['categories'])
      setDeleteId(null)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to delete category'
      toast.error(message)
    },
  })

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim()) {
      toast.error('Category name is required')
      return
    }
    setError(null)
    createMutation.mutate({name: newName})
  }

  const handleUpdate = (id: string | number) => {
    if (!editName.trim()) {
      toast.error('Category name is required')
      return
    }
    setError(null)
    updateMutation.mutate({id, name: editName})
  }

  const startEditing = (category: Category) => {
    setEditingId(category.id as string | number)
    setEditName(category.name)
    setError(null)
  }

  if (isLoading) {
    return (
      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          <div className='bg-white border border-gray-200 rounded-lg shadow-sm'>
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>Categories</span>
              </h3>
            </div>
            <div className='p-4'>
              <div className='overflow-x-auto'>
                <table className='min-w-full table-auto border-separate border-spacing-y-1 text-sm'>
                  <thead>
                    <tr className='text-left text-white font-semibold text-xs uppercase tracking-wider bg-[#008080]'>
                      <th className='px-4 py-3.5 rounded-tl-lg'>Name</th>
                      <th className='px-4 py-3.5'>Slug</th>
                      <th className='px-4 py-3.5'>Docs Count</th>
                      <th className='px-4 py-3.5 rounded-tr-lg text-right'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='text-gray-700 font-semibold'>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <tr key={i} className='even:bg-[#e6f2f2] odd:bg-white'>
                        <td className='px-4 py-3.5'><Skeleton width='70%' height={16} /></td>
                        <td className='px-4 py-3.5'><Skeleton width='50%' height={12} /></td>
                        <td className='px-4 py-3.5'><Skeleton width={40} height={12} /></td>
                        <td className='px-4 py-3.5 text-right'>
                          <div className='flex items-center justify-end gap-1'>
                            <Skeleton circle width={28} height={28} />
                            <Skeleton circle width={28} height={28} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='row g-5 g-xl-8'>
      <div className='col-xl-12'>
        <KTCard className='border-gray-200 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1 mr-3'>Categories</span>
              <span className='text-[#008080] font-bold mt-1 fw-semibold fs-7'>{categories?.length || 0} categories total</span>
            </h3>
            <div className='card-toolbar my-5'>
              <button
                type='button'
                className='inline-flex items-center gap-2 px-4 py-2 bg-[#ccf2f2] text-[#008080] text-sm font-medium rounded-lg hover:bg-[#b3e6e6] transition-colors duration-200'
                onClick={() => {
                  setIsAdding(true)
                  setEditingId(null)
                  setError(null)
                }}
              >
                <Plus className='w-4 h-4' />
                New Category
              </button>
            </div>
          </div>

          <KTCardBody className='py-3'>
            {isAdding && (
              <form onSubmit={handleAdd} className='mb-6 p-5 border border-[#008080]/20 rounded-lg bg-[#e6f2f2]'>
                <label className='block mb-2 text-sm font-medium text-gray-700'>Category Name</label>
                <div className='flex items-center gap-3'>
                  <input
                    type='text'
                    className='form-control form-control-solid flex-1 focus:outline-none focus:ring-2 focus:ring-[#008080]/30 focus:border-[#008080] transition-all border border-gray-200 py-2.5 px-5'
                    placeholder='Enter category name'
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    autoFocus
                  />
                  <button
                    type='submit'
                    className='inline-flex items-center gap-2 px-4 py-2 bg-[#008080] text-white text-sm font-medium rounded-lg hover:bg-[#007373] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                    disabled={createMutation.isLoading}
                  >
                    {createMutation.isLoading ? 'Adding...' : 'Add'}
                  </button>
                  <button
                    type='button'
                    className='inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200'
                    onClick={() => {
                      setIsAdding(false)
                      setError(null)
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            <div className='overflow-x-auto'>
              <table className='min-w-full table-auto border-separate border-spacing-y-0.5 text-sm'>
                <thead>
                  <tr className='text-left text-white font-semibold text-xs uppercase tracking-wider bg-[#008080]'>
                    <th className='px-4 py-3.5 rounded-tl-lg'>Name</th>
                    <th className='px-4 py-3.5'>Slug</th>
                    <th className='px-4 py-3.5'>Docs Count</th>
                    <th className='px-4 py-3.5 rounded-tr-lg text-right'>Actions</th>
                  </tr>
                </thead>
                <tbody className='text-gray-700 font-semibold'>
                  {categories?.length === 0 ? (
                    <tr>
                      <td colSpan={4} className='text-center text-gray-400 py-12'>
                        No categories found.
                      </td>
                    </tr>
                  ) : (
                    categories?.map((category) => (
                      <tr
                        key={category.id}
                        className='even:bg-[#e6f2f2] odd:bg-white hover:bg-[#ccf2f2]/50 transition-colors duration-150'
                      >
                        <td className='px-4 py-3.5'>
                          {editingId === category.id ? (
                            <div className='flex flex-col'>
                              <div className='flex items-center gap-2'>
                                <input
                                  type='text'
                                  className='form-control form-control-sm form-control-solid focus:outline-none focus:ring-2 focus:ring-[#008080]/30 focus:border-[#008080] transition-all py-1 px-3 h-9'
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleUpdate(category.id as string | number)
                                    if (e.key === 'Escape') setEditingId(null)
                                  }}
                                />
                                <button
                                  className='inline-flex items-center justify-center min-w-[36px] h-9 rounded-lg bg-[#ccf2f2] text-[#008080] hover:bg-[#b3e6e6] transition-colors duration-200'
                                  onClick={() => handleUpdate(category.id as string | number)}
                                  title='Save'
                                >
                                  <Check className='w-4 h-4' />
                                </button>
                                <button
                                  className='inline-flex items-center justify-center min-w-[36px] h-9 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors duration-200'
                                  onClick={() => {
                                    setEditingId(null)
                                    setError(null)
                                  }}
                                  title='Cancel'
                                >
                                  <X className='w-4 h-4' />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span
                              className='text-gray-800 font-bold hover:text-[#008080] transition-colors duration-200 cursor-pointer text-sm'
                              onClick={() => startEditing(category)}
                            >
                              {category.name}
                            </span>
                          )}
                        </td>
                        <td className='px-4 py-3.5'>
                          <span className='text-gray-400 font-medium text-xs'>{category.slug}</span>
                        </td>
                        <td className='px-4 py-3.5'>
                          <span className='text-gray-400 font-medium text-xs'>
                            {category.docs?.length || 0}
                          </span>
                        </td>
                        <td className='px-4 py-3.5 text-right'>
                          <div className='flex items-center justify-end gap-1'>
                            <button
                              className='inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#ccf2f2] text-[#008080] hover:bg-[#b3e6e6] transition-colors duration-200'
                              onClick={() => startEditing(category)}
                              title='Edit'
                            >
                              <Pencil className='w-4 h-4' />
                            </button>
                            <button
                              className='inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#ccf2f2] text-[#008080] hover:bg-[#b3e6e6] transition-colors duration-200'
                              onClick={() => setDeleteId(category.id as string | number)}
                              title='Delete'
                            >
                              <Trash2 className='w-4 h-4' />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </KTCardBody>
        </KTCard>
      </div>

      <DeleteModal
        showDeleteConfirm={deleteId !== null}
        setShowDeleteConfirm={(show) => { if (!show) setDeleteId(null) }}
        deleteAction={async (id: any) => { await deleteMutation.mutateAsync(id) }}
        refetch={() => queryClient.invalidateQueries(['categories'])}
        id={deleteId!}
      />
    </div>
  )
}

export default CategoriesList
